import {
  getFirstNameAndLastName,
  isHubspotDebuggingEnabled,
  objectToSearchParams,
  removeUndefinedText,
  windowOpen,
} from './jsHelper';

import { API_ROOT_URL, GET_AI_CURATOR_JSON_API, UPDATE_PRODUCT_RENDER_JSON } from '../config/constant/apiConstant';
import api, { apiFetch } from './axiosHelper';
import { v4 as uuidv4 } from 'uuid';
import { RESTRICTED_EMAILS } from '../config/constant/projectConstant';
import { LIVE_FURNISH } from '../constants/projectConstants';
import { apiPatch } from './api';
import { errorToastV2 } from './toastHelper';

export const createListApiRequest =
  ({ url, onStart, onSuccess, onError, startAction, successAction, errorAction, actions }) =>
  ({ search, sort = '-created_at', perPage, page, ...rest } = {}) =>
  (dispatch) => {
    const searchParams = objectToSearchParams({
      search,
      sort,
      per_page: perPage,
      page,
      ...rest,
    });
    const API_URL = `${url}?${searchParams}`;

    onStart?.(dispatch);
    startAction && dispatch(startAction());

    api()
      .root(API_ROOT_URL)
      .get(API_URL)
      .success((data) => {
        onSuccess?.({
          data,
          dispatch,
          args: {
            search,
            sort,
            perPage,
            page,
          },
        });

        successAction &&
          dispatch(
            successAction({
              page,
              data,
            })
          );
      })
      .error((error) => {
        // TODO: add logger
        console.error(error);
        onError?.(error, dispatch);
        errorAction && dispatch(errorAction(error));
      })
      .send();
  };

export const fetchAllPages = async (api, callToPage = null) => {
  const data = await api(1);
  if (!data) {
    return null;
  }

  if (data?.total_pages === 1) {
    return data;
  }

  const pages = Array.from({ length: (callToPage || data.total_pages) - 1 }, (_, i) => i + 2);

  const list = await Promise.all(
    pages.map(async (page) => {
      const result = await api(page);
      return result.results;
    })
  );

  const reducedList = list.reduce((acc, items) => [...acc, ...items], []);
  return {
    ...data,
    results: [...(data?.results || []), ...(reducedList || [])],
  };
};

export const fetchAllPagesByIdList = async ({ api, idList, perPage = 20 }) => {
  const paginatedIdList = [];
  let arrIndex = -1;
  idList.forEach((id, idx) => {
    if (idx % perPage === 0) {
      paginatedIdList.push([]);
      arrIndex += 1;
    }

    paginatedIdList[arrIndex].push(id);
  });

  const items = [];
  await Promise.all(
    paginatedIdList.map(async (ids) => {
      const result = await api(ids);
      if (Array.isArray(result?.results)) {
        items.push(...result.results);
      }
    })
  );

  return items;
};

export const getUTMParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  return {
    utm_source: searchParams?.get('utm_source') || ' ',
    utm_campaign: searchParams?.get('utm_campaign') || ' ',
    utm_medium: searchParams?.get('utm_medium') || ' ',
    utm_content: searchParams?.get('utm_content') || ' ',
    utm_term: searchParams?.get('utm_term') || ' ',
  };
};

/*
 * Note: Whenever you want to send the information to the hubspot platform using function hubspotAnaliticsInfo & ManuallyFormAPI
 * then, you need to create the field in the hubspot form as well ( form link: https://app.hubspot.com/forms/6917454/editor/3d162ed1-5028-4054-ba1f-d483effa4d19/edit/form )
 */

// hubspotAnaliticsInfo is used to store new sign-up user basic details
export const hubspotAnaliticsInfo = (_values = {}) => {
  const values = { ..._values, ...getUTMParams() };
  // prevent email submission for internal users
  const isExists = RESTRICTED_EMAILS.some((a) => values?.email?.toLowerCase()?.trim()?.includes(a));

  if (isHubspotDebuggingEnabled() || !isExists) {
    const { firstName, lastName } = getFirstNameAndLastName(values?.full_name, true);
    const data = {
      fields: [
        { name: 'email', value: values?.email },
        {
          name: 'firstname',
          value: `${firstName || values?.first_name || ''} ${
            lastName?.trim() || removeUndefinedText(values?.last_name) || ''
          }`,
        },
        { name: 'lastname', value: ' ' },
        {
          name: 'signed_up',
          value: values?.signed_up || values?.signed_up === false ? values?.signed_up : true,
        },
        values?.b2c_attempt ? { name: 'b2c_attempt', value: values?.b2c_attempt } : null, // passing 'b2c_attempt' only when it's value is true. otherwise, it update the previous value
        values?.is_invited ? { name: 'is_invited', value: values?.is_invited } : null, // passing 'is_invited' only when it's value is true. otherwise, it update the previous value
        { name: 'company', value: values?.company_name || ' ' },
        { name: 'phone', value: values?.mobile_number || ' ' },
        { name: 'ipaddress', value: values?.clientIP || ' ' },
        { name: 'utm_campaign', value: values?.utm_campaign || ' ' },
        { name: 'utm_source', value: values?.utm_source || ' ' },
        { name: 'utm_medium', value: values?.utm_medium || ' ' },
        { name: 'utm_content', value: values?.utm_content || ' ' },
        { name: 'utm_term', value: values?.utm_term || ' ' },
      ].filter(Boolean),
      context: {
        hutk: getCookie(), // include this parameter and set it to the hubspotutk cookie value to enable cookie tracking on your submission
        pageUri: window.location.href,
        pageName: document.title,
      },
    };

    const xhr = new XMLHttpRequest();
    xhr.open(
      'POST',
      'https://api.hsforms.com/submissions/v3/integration/submit/6917454/3d162ed1-5028-4054-ba1f-d483effa4d19',
      true
    );
    xhr.setRequestHeader('Content-Type', 'application/json');

    xhr.onreadystatechange = function () {
      if (xhr.readyState === 4 && xhr.status === 200) {
        console.log('Form submitted!');
      }
    };

    xhr.send(JSON.stringify(data));
  }
};

window.hubspotAnaliticsInfo = (v) => hubspotAnaliticsInfo(v);

/*
 * Note: Whenever you want to send the information to the hubspot platform using function hubspotAnaliticsInfo & ManuallyFormAPI
 * then, you need to create the field in the hubspot form as well ( form link: https://app.hubspot.com/forms/6917454/editor/3d162ed1-5028-4054-ba1f-d483effa4d19/edit/form )
 */

// ManuallyFormAPI is used for store the UTM data from URL into hubspot platform for analytics.
export const ManuallyFormAPI = (_values = {}) => {
  const values = { ..._values, ...getUTMParams() };
  const isExists = RESTRICTED_EMAILS.some((a) => values?.email?.toLowerCase()?.trim()?.includes(a));
  if (values && window.location?.search && (isHubspotDebuggingEnabled() || !isExists)) {
    const { firstName, lastName } = getFirstNameAndLastName(values?.full_name, true);
    const data = {
      contactFields: {
        email: values?.email,
        firstName: `${firstName || values?.first_name || ''} ${
          lastName || removeUndefinedText(values?.last_name) || ''
        }`,
        company: values?.company_name || ' ',
        phone: values?.mobile_number || ' ',
        ipaddress: values?.clientIP || ' ',
        ...(values?.b2c_attempt ? { b2c_attempt: values?.b2c_attempt } : {}), // passing 'b2c_attempt' only when it's value is true. otherwise, it update the previous value
        ...(values?.is_invited ? { is_invited: values?.is_invited } : {}), // passing 'is_invited' only when it's value is true. otherwise, it update the previous value
        utm_campaign: values?.utm_campaign || ' ',
        utm_source: values?.utm_source || ' ',
        utm_medium: values?.utm_medium || ' ',
        utm_content: values?.utm_content || ' ',
        utm_term: values?.utm_term || ' ',
      },
      formSelectorId: '#myform',
      collectedFormId: '#myform',
      formSelectorClasses: '',
      pageTitle: window?.document?.title,
      formValues: {},
      labelToNameMap: {},
      portalId: '6917454',
      type: 'SCRAPED',
      utk: getCookie(),
      uuid: uuidv4(),
      pageUrl: window.location?.href,
      version: 'collected-forms-embed-js-static-1.378',
    };

    const xhr = new XMLHttpRequest();
    xhr.open('POST', 'https://forms.hscollectedforms.net/collected-forms/submit/form', true);
    xhr.setRequestHeader('Content-Type', 'application/json');

    xhr.onreadystatechange = function () {
      if (xhr.readyState === 4 && xhr.status === 200) {
        console.log('Form value collected!');
      }
    };
    xhr.send(JSON.stringify(data));
  }
};

export const sendDataToHubspotForRestrictedUser = ({
  user = null,
  callback = null,
  redirectToWebsite = true,
  openChat = true,
} = {}) => {
  if (!user) return;
  callback?.();
  const restrictedUser = {
    email: user?.email,
    first_name: user?.first_name,
    last_name: user?.last_name,
    b2c_attempt: true,
    signed_up: false,
    is_invited: user?.is_invited || false,
  };
  ManuallyFormAPI(restrictedUser);
  setTimeout(() => hubspotAnaliticsInfo?.(restrictedUser), 200);
  if (redirectToWebsite) {
    const link = openChat ? LIVE_FURNISH.websiteWithChatOpen : LIVE_FURNISH.website;
    setTimeout(() => windowOpen(link, '_blank'), 3000);
  }
};

/*
 * Note: Whenever you want to send the information to the hubspot platform using function sendInvitedUserToHubspot
 * then, you need to create the field in the hubspot form as well ( form link: https://app.hubspot.com/forms/6917454/editor/75345df5-bccf-441c-a9db-1b88eb2446ef/edit/form )
 */

// 'sendInvitedUserToHubspot' is used to send the invited users email to the husbpot
export const sendInvitedUserToHubspot = (_values = {}) => {
  const { firstName, lastName } = getFirstNameAndLastName(_values?.name, true);

  const values = {
    ..._values,
    firstname: _values?.firstname || firstName,
    lastname: _values?.lastname || lastName,
  };

  if (values?.email && values?.invited_users) {
    const data = {
      fields: [
        { name: 'email', value: values?.email },
        {
          name: 'firstname',
          value: values?.firstname || ' ',
        },
        { name: 'lastname', value: values?.lastname || ' ' },
        {
          name: 'company',
          value: values?.company || ' ',
        },
        { name: 'invited_users', value: values?.invited_users },
      ],
      context: {
        hutk: getCookie(), // include this parameter and set it to the hubspotutk cookie value to enable cookie tracking on your submission
        pageUri: window.location.href,
        pageName: document.title,
      },
    };

    const xhr = new XMLHttpRequest();
    xhr.open(
      'POST',
      'https://api.hsforms.com/submissions/v3/integration/submit/6917454/75345df5-bccf-441c-a9db-1b88eb2446ef',
      true
    );
    xhr.setRequestHeader('Content-Type', 'application/json');

    xhr.onreadystatechange = function () {
      if (xhr.readyState === 4 && xhr.status === 200) {
        console.log('Form submitted!');
      }
    };

    xhr.send(JSON.stringify(data));
  }
};

export const deleteCookies = (cookieNames = []) => {
  if (!Array.isArray(cookieNames) || cookieNames?.length === 0) return; // Exit if cookieNames is not an array or is empty

  const d = new Date(); // Create a new date object
  d.setTime(d.getTime() - 1); // Set the time to a past date
  const expires = 'expires=' + d.toUTCString(); // Create an expiration string

  // Iterate over each cookie name in the array and delete it
  cookieNames.forEach((cookieName) => {
    if (cookieName) {
      // Check if cookieName is valid
      document.cookie = `${cookieName}=; ${expires}; path=/`; // Set the cookie with the past expiration date
    }
  });
};

export const getAndSetCookieData = ({
  cookieName = null,
  cookieData = null,
  cookieExpireTime = 86400000, // 1 day in milliseconds
} = {}) => {
  if (!cookieName) return;

  const _cookieName = `${cookieName}=`;

  try {
    if (cookieData !== null) {
      // Serialize and store data as a cookie
      // Set the cookie to expire in cookieExpireTime
      const d = new Date();
      d.setTime(d.getTime() + cookieExpireTime);
      const expires = 'expires=' + d.toUTCString();
      document.cookie =
        _cookieName + encodeURIComponent(JSON.stringify(cookieData)) + ';' + expires + ';path=/';
    }

    // Retrieve and parse the cookie data
    const decodedCookies = decodeURIComponent(document.cookie);
    const ca = decodedCookies.split(';');
    for (let i = 0; i < ca.length; i++) {
      const c = ca[i].trim();
      if (c.indexOf(_cookieName) == 0) {
        return JSON.parse(c.substring(_cookieName.length, c.length));
      }
    }
    return null;
  } catch (error) {
    console.error('Error in getAndSetCookieData:', error);
    return null;
  }
};

export const getCookie = () => {
  const cookies = window.document.cookie.split(';');
  let value = null;
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();

    if (cookie.startsWith('hubspotutk=')) {
      const cookieValue = cookie.substring('hubspotutk='.length);
      value = cookieValue;
    }
  }
  return value;
};

export async function loadImageAsync(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => reject(false);
    img.src = url;
  });
}

export const getAiCuratorJsonApi = async (id) => {
  const url = `${GET_AI_CURATOR_JSON_API}${id}/`;
  const meta = await apiFetch(url).send();
  const metaPayload = meta?.data || meta?.result || meta || {};
  return (
    metaPayload?.curator_json_file ||
    metaPayload?.data?.curator_json_file ||
    metaPayload?.result?.curator_json_file
  );
};

export const updateRenderAPI = async ({ id, payload }) => {
  const url = `${UPDATE_PRODUCT_RENDER_JSON}${id}/`;
  const result = await apiPatch(url, payload);
  if (!result?.data) {
    errorToastV2('Something went wrong. Please try again.');
    return;
  }
  return result?.data;
};